import { useAnimation } from 'framer-motion';
import _, { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';

import ContentMedia from 'components/ContentMedia/ContentMedia';
import VideoBlock from 'components/VideoBlock/VideoBlock';
import {
  CMSSectionContentType,
  MediaBlockCMS,
} from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from './MediaSlider.styles';

export interface MediaSliderProps {
  media: Array<MediaBlockCMS>;
  gap: number;
}

const MEDIA_COMPONENTS: {
  [key in CMSSectionContentType]?: React.FunctionComponent<any>;
} = {
  image: ContentMedia,
  video: VideoBlock,
};

const MediaSlider: React.FunctionComponent<MediaSliderProps> = ({
  media,
  gap = 17,
  ...rest
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  const [slides, setSlides] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [variants, setVariants] = useState({});
  const [dragPosition, setDragPosition] = useState(0);

  const onDragStart = e => {
    setDragPosition(e.x);
  };

  const onDragEnd = e => {
    let s = currentSlide;

    if (e.x < dragPosition) {
      s = s < slides ? s + 1 : s;
    } else if (e.x > dragPosition) {
      s = s >= 1 ? s - 1 : s;
    }

    setCurrentSlide(s);
    controls.start(`position_${currentSlide}`);
  };

  useEffect(() => {
    controls.start(`position_${currentSlide}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  useEffectOnce(() => {
    const wrapperWidth = wrapperRef.current.getBoundingClientRect().width;
    const carouselWidth = carouselRef.current.getBoundingClientRect().width;

    const availableSlides = carouselWidth / wrapperWidth;
    const fullSlides = Math.floor(availableSlides);
    setSlides(fullSlides);

    const singleScrollMovement = carouselWidth / availableSlides;
    const clampedMovement =
      _.clamp(
        singleScrollMovement * fullSlides,
        0,
        carouselWidth - wrapperWidth
      ) / fullSlides;

    const scrollVariants = Object.fromEntries(
      _.times(fullSlides, slide => [
        `position_${slide + 1}`,
        {
          x: -(clampedMovement * (slide + 1)),
        },
      ])
    );

    setVariants({
      position_0: { x: 0 },
      ...scrollVariants,
    });
  });

  return (
    <S.MediaSliderWrapper {...rest}>
      <S.DotsWrapper>
        {_.times(slides + 1, index => (
          <S.Dot
            key={uniqueId()}
            onClick={() => setCurrentSlide(index)}
            isActive={index === currentSlide}
          />
        ))}
      </S.DotsWrapper>
      <S.MediaSliderBox ref={wrapperRef}>
        <S.MediaSliderStyled
          slides={media.length}
          drag="x"
          animate={controls}
          onDragEnd={onDragEnd}
          ref={carouselRef}
          onDragStart={onDragStart}
          variants={variants}
          dragConstraints={wrapperRef}
          gap={gap}
        >
          {media.map(m => {
            const Component = MEDIA_COMPONENTS[m.contentType];
            if (!Component) return null;

            return (
              <S.ComponentWrapper key={m.id}>
                <Component {...m} />
              </S.ComponentWrapper>
            );
          })}
        </S.MediaSliderStyled>
      </S.MediaSliderBox>
    </S.MediaSliderWrapper>
  );
};

export default MediaSlider;
